import React, { useMemo, useState } from "react";
import { TextField, Typography, Box, Paper, Grid2 } from "@mui/material";
import { styled } from "@mui/material/styles";
import { Theme } from "@mui/material/styles/createTheme";
import "./index.css";
import DonutChart from "../../Components/DonutChart";
import BannerComponent from "../../Components/BannerComponent";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import PercentIcon from "@mui/icons-material/Percent";
import useScreenSize from "../../hooks/useScreenSize";

const StyledPaper = styled(Paper)(({ theme }: { theme: Theme }) => ({
  padding: theme.spacing(4),
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[5],
  backgroundColor: theme.palette.background.default,
}));

const EMICalculator: React.FC = () => {
  const [principal, setPrincipal] = useState<string>();
  const [rate, setRate] = useState<string>("");
  const [tenure, setTenure] = useState<string>("");
  const { isLessThanMd } = useScreenSize();

  const calculateEMI = useMemo(() => {
    if (Number(principal) > 0 && Number(rate) > 0 && Number(tenure) > 0) {
      const principalAmount = Number(principal);
      const annualRate = Number(rate);
      const months = Number(tenure);

      // Flat rate interest calculation
      const interestAmount = (principalAmount * annualRate) / 100;
      const totalInterest = interestAmount * (months / 12); // Total interest based on tenure in years

      // Total amount payable
      const totalAmount = principalAmount + totalInterest;

      // EMI calculation
      const emi = totalAmount / months;

      return {
        principleAmount: principalAmount,
        interestAmount: totalInterest,
        totalAmount,
        monthlyEmi: emi.toFixed(2),
      };
    }
    return {
      principleAmount: 0,
      interestAmount: 0,
      totalAmount: 0,
      monthlyEmi: 0,
    };
  }, [principal, rate, tenure]);

  const handleChangePrincipalAmount = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newValue = e.target.value;
    if (/^\d*$/.test(newValue)) {
      setPrincipal(newValue);
    }
  };

  const handleChangeAnnualInterest = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newValue = e.target.value;
    if (/^\d*\.?\d*$/.test(newValue)) {
      setRate(newValue); // Update the state with the new value
    }
  };

  const handleChangeTenure = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    if (/^\d*$/.test(newValue)) {
      setTenure(newValue); // Directly set tenure as months
    }
  };

  return (
    <Box
      className=""
      sx={{
        margin: isLessThanMd ? "0px 20px 50px 20px" : "0px 0px 50px 0px",
      }}
    >
      <Box className="container">
        <BannerComponent
          sectionTitle="EMI Calculator"
          title="Quick EMI Calculation for Smarter Planning"
          body="Find out your exact EMI and stay on top of your loan repayments."
        />
      </Box>
      <Box className="container section-divide">
        <Grid2 container>
          <Grid2 container size={{ xs: 12, md: 6 }}>
            <StyledPaper elevation={3}>
              <Grid2 container spacing={2}>
                <Grid2 size={{ xs: 12 }}>
                  <TextField
                    label="Principal Amount"
                    variant="outlined"
                    fullWidth
                    type="text"
                    value={principal}
                    onChange={handleChangePrincipalAmount}
                    slotProps={{
                      input: {
                        startAdornment: <CurrencyRupeeIcon />,
                      },
                    }}
                  />
                </Grid2>
                <Grid2 size={{ xs: 12 }}>
                  <TextField
                    label="Annual Interest Rate (%)"
                    variant="outlined"
                    fullWidth
                    type="text"
                    value={rate}
                    onChange={handleChangeAnnualInterest}
                    slotProps={{
                      input: {
                        endAdornment: <PercentIcon />,
                      },
                    }}
                  />
                </Grid2>
                <Grid2 size={{ xs: 12 }}>
                  <TextField
                    label="Tenure (Months)"
                    variant="outlined"
                    fullWidth
                    type="text"
                    value={tenure}
                    slotProps={{
                      input: {
                        endAdornment: <Typography>Months</Typography>,
                      },
                    }}
                    onChange={handleChangeTenure}
                  />
                </Grid2>
                <Box mt={3} sx={{ width: "100%" }}>
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography variant="h6" gutterBottom>
                      Your Monthly EMI is:
                    </Typography>
                    <Typography variant="h6" gutterBottom>
                      <strong>₹{calculateEMI.monthlyEmi}</strong>
                    </Typography>
                  </Box>
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography variant="h6" gutterBottom>
                      Total Interest:
                    </Typography>
                    <Typography variant="h6" gutterBottom>
                      <strong>₹{calculateEMI.interestAmount}</strong>
                    </Typography>
                  </Box>
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography variant="h6" gutterBottom>
                      Total Amount Payable:
                    </Typography>
                    <Typography variant="h6" gutterBottom>
                      <strong>₹{calculateEMI.totalAmount}</strong>
                    </Typography>
                  </Box>
                </Box>
              </Grid2>
            </StyledPaper>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <DonutChart
              principalAmount={calculateEMI.principleAmount}
              interestAmount={calculateEMI.interestAmount}
            />
          </Grid2>
        </Grid2>
      </Box>
    </Box>
  );
};

export default EMICalculator;
